import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from './cn';
import { needsFade } from './overflowChipsUtils';

export type OverflowChipsProps = {
  items: ReactNode[];
  readOnly?: boolean;
};

export function OverflowChips({ items, readOnly = false }: OverflowChipsProps) {
  const rowRef = useRef<HTMLDivElement | null>(null);
  const [fade, setFade] = useState(false);

  const syncFade = useCallback(() => {
    const el = rowRef.current;
    if (!el) return;
    setFade(needsFade(el.scrollWidth, el.clientWidth));
  }, []);

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    syncFade();
    const ro = new ResizeObserver(syncFade);
    ro.observe(el);
    return () => ro.disconnect();
  }, [items.length, syncFade]);

  if (items.length === 0) {
    return <span className="min-w-0" aria-hidden />;
  }

  const inline = (
    <div ref={rowRef} className={cn('fonoteka-pills', fade && 'fonoteka-pills_fade')}>
      {items}
    </div>
  );

  if (readOnly) {
    return inline;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="fonoteka-chip-cell"
          onClick={(e) => e.stopPropagation()}
        >
          {inline}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-max max-w-[min(20rem,80vw)] p-2"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col items-start gap-[0.28rem]">{items}</div>
      </PopoverContent>
    </Popover>
  );
}
